import React, { useState, useEffect } from "react";

import AirbyteApi from "apis/airbyte";
import { Pane, Typography, Toastr, PageLoader } from "clearsense-ui/components";
import { useAirbyteState } from "contexts/airbyte";

import ConnectionDetails from "./ConnectionDetails";
import Footer from "./Footer";
import Jobs from "./Jobs";
import Transformations from "./Transformations";

import {
  FREQUENCY_OPTIONS,
  MANUAL_FREQUENCY_OPTION,
  NAMESPACE_OPTIONS,
} from "../constants";
import Delete from "../Delete";
import DeleteAlert from "../Delete/Alert";
import Replication from "../Replication";
import Streams from "../Streams";

const EditConnection = ({ close, connection, fetchConnections }) => {
  const [loading, setLoading] = useState();
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [streams, setStreams] = useState([]);
  const [operations, setOperations] = useState(false);
  const [selectedSource, setSelectedSource] = useState();
  const [status, setStatus] = useState(false);
  const [initialStreamValues, setInitialStreamValues] = useState({});
  const [replication, setReplication] = useState();
  const [isConnectionSetup, setIsConnectionSetup] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);

  const { workspaceId } = useAirbyteState();

  const serializedJson = [
    {
      name: "Normalization",
      workspaceId,
      operatorConfiguration: {
        operatorType: "normalization",
        normalization: {
          option: "basic",
        },
      },
    },
  ];

  const fetchConnection = async () => {
    setLoading(true);
    try {
      const {
        data: {
          syncCatalog,
          status,
          schedule,
          sourceId,
          operations,
          namespaceDefinition,
          namespaceFormat,
        },
      } = await AirbyteApi.fetchConnectionDetails({
        connectionId: connection?.key,
      });
      setOperations(operations?.length > 0);
      setSelectedSource(sourceId);
      getReplicationValues(namespaceDefinition, namespaceFormat, schedule);
      const streamNames = syncCatalog.streams.map(stream => stream.stream.name);
      const values = streamNames.reduce(
        (accumulator, index) => ((accumulator[index] = true), accumulator),
        {}
      );
      setStatus(status === "active");
      setInitialStreamValues(values);
      setStreams(syncCatalog.streams);
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getReplicationValues = (definition, namespaceFormat, schedule) => {
    const frequency = schedule
      ? FREQUENCY_OPTIONS.filter(
          option => option.value?.units === schedule?.units
        )[0]
      : MANUAL_FREQUENCY_OPTION;
    const namespaceDefinition = NAMESPACE_OPTIONS.filter(
      option => option.value === definition
    )[0];
    setReplication({
      namespaceDefinition,
      namespaceFormat,
      frequency,
    });
  };

  const handleConnection = async () => {
    const payload = { sourceId: selectedSource };
    try {
      setCatalogLoading(true);
      const { data } = await AirbyteApi.fetchCatalog(payload);
      const streamNames = data.catalog.streams.map(item => item.stream.name);
      const values = streamNames.reduce(
        (accumulator, name) => ((accumulator[name] = true), accumulator),
        {}
      );
      setInitialStreamValues(values);
      setStreams(data?.catalog?.streams);
    } catch (error) {
      Toastr.error("Error fetching streams");
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      connectionId: connection?.key,
      status: status ? "active" : "inactive",
      syncCatalog: { streams: streams },
      prefix: "",
      namespaceDefinition: replication?.namespaceDefinition?.value,
      namespaceFormat: replication?.namespaceFormat ?? "${SOURCE_NAMESPACE}",
      schedule: replication?.frequency?.value,
      operations: operations ? serializedJson : [],
    };
    try {
      setLoading(true);
      await AirbyteApi.updateConnection(payload);
      Toastr.success("Connection updated successfully");
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleClose = () => {
    setStreams([]);
    setOperations(false);
    setSelectedSource();
    setIsDeleteAlertOpen(false);
    setIsConnectionSetup(false);
    close();
  };

  useEffect(() => {
    if (connection) fetchConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection]);

  return (
    <>
      <Pane
        isOpen={!!connection}
        onClose={handleClose}
        className="h-full"
        size="lg"
      >
        <Pane.Header>
          <Typography style="h2" type="bold" data-cy="edit-connection-title">
            Edit a Connection
          </Typography>
        </Pane.Header>
        <Pane.Body className="block h-full mt-4 space-y-4">
          {loading ? (
            <PageLoader />
          ) : (
            <>
              <ConnectionDetails
                connection={connection}
                status={status}
                setStatus={setStatus}
              />
              <Replication
                replication={replication}
                setReplication={setReplication}
              />
              <Streams
                streams={streams}
                setStreams={setStreams}
                initialStreamValues={initialStreamValues}
                isConnectionSetup={isConnectionSetup}
                setIsConnectionSetup={setIsConnectionSetup}
                isLoading={catalogLoading}
                handleConnection={handleConnection}
              />
              <Jobs connectionId={connection?.key} />
              <Transformations
                operations={operations}
                setOperations={setOperations}
              />
              <Delete setIsDeleteAlertOpen={setIsDeleteAlertOpen} />
            </>
          )}
        </Pane.Body>
        <Pane.Footer className="items-center justify-end mt-6 space-x-4">
          <Footer close={handleClose} submit={handleSubmit} />
        </Pane.Footer>
      </Pane>

      <DeleteAlert
        connectionId={connection?.key}
        isDeleteAlertOpen={isDeleteAlertOpen}
        setIsDeleteAlertOpen={setIsDeleteAlertOpen}
        handlePaneClose={close}
        fetchConnections={fetchConnections}
      />
    </>
  );
};

export default EditConnection;
